import { useState, useRef, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Camera, FileUp, Check, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type ProofType = 'NONE' | 'TEXT_NOTE' | 'FILE_UPLOAD' | 'PHOTO_UPLOAD' | 'FORM';

interface ProofCaptureSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proofType: ProofType;
  taskTitle: string;
  taskId: string;
  userId: string;
  onSubmit: (proof: { type: ProofType; value: string; fileUrl?: string }) => Promise<void>;
  onSkip?: () => void;
}

export function ProofCaptureSheet({
  open,
  onOpenChange,
  proofType,
  taskTitle,
  taskId,
  userId,
  onSubmit,
  onSkip,
}: ProofCaptureSheetProps) {
  const { toast } = useToast();
  const [textValue, setTextValue] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const resetState = useCallback(() => {
    setTextValue('');
    setFile(null);
    setPreviewUrl(null);
    setIsCameraActive(false);
    setUploadProgress(0);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onOpenChange(false);
  }, [resetState, onOpenChange]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Create preview for images
      if (selectedFile.type.startsWith('image/')) {
        const url = URL.createObjectURL(selectedFile);
        setPreviewUrl(url);
      }
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsCameraActive(true);
    } catch (error) {
      console.error('Camera access denied:', error);
      // Fallback to file input
      fileInputRef.current?.click();
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) {
          const photoFile = new File([blob], `proof-${Date.now()}.jpg`, { type: 'image/jpeg' });
          setFile(photoFile);
          setPreviewUrl(canvas.toDataURL('image/jpeg'));
          // Stop camera
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
          }
          setIsCameraActive(false);
        }
      }, 'image/jpeg', 0.9);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      let fileUrl: string | undefined;

      // Upload file to storage if applicable
      if ((proofType === 'PHOTO_UPLOAD' || proofType === 'FILE_UPLOAD') && file) {
        setUploadProgress(10);
        
        // SECURITY: Get organization ID for org-scoped storage path
        const { data: membership } = await supabase
          .from('memberships')
          .select('organization_id')
          .eq('user_id', userId)
          .single();
        
        const orgId = membership?.organization_id || 'unknown';
        const fileExt = file.name.split('.').pop() || 'jpg';
        const fileName = `${orgId}/${taskId}/${Date.now()}.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('proof-attachments')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          throw new Error(`Upload failed: ${uploadError.message}`);
        }

        setUploadProgress(80);

        // Get public URL
        const { data: urlData } = supabase.storage
          .from('proof-attachments')
          .getPublicUrl(fileName);

        fileUrl = urlData.publicUrl;
        setUploadProgress(100);
      }

      // Submit proof
      if (proofType === 'TEXT_NOTE' && textValue.trim()) {
        await onSubmit({ type: proofType, value: textValue.trim() });
      } else if ((proofType === 'PHOTO_UPLOAD' || proofType === 'FILE_UPLOAD') && file && fileUrl) {
        await onSubmit({ type: proofType, value: file.name, fileUrl });
      }

      // Haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate(10);
      }

      handleClose();
    } catch (error: any) {
      console.error('Error submitting proof:', error);
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: error.message || 'Could not upload proof. Try again.',
      });
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const canSubmit = 
    (proofType === 'TEXT_NOTE' && textValue.trim().length > 0) ||
    ((proofType === 'PHOTO_UPLOAD' || proofType === 'FILE_UPLOAD') && file);

  const getIcon = () => {
    switch (proofType) {
      case 'PHOTO_UPLOAD': return <Camera className="h-5 w-5" />;
      case 'FILE_UPLOAD': return <FileUp className="h-5 w-5" />;
      default: return null;
    }
  };

  const getTitle = () => {
    switch (proofType) {
      case 'TEXT_NOTE': return 'Add a note';
      case 'PHOTO_UPLOAD': return 'Take a photo';
      case 'FILE_UPLOAD': return 'Upload a file';
      default: return 'Add proof';
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="bottom" className="h-auto max-h-[85vh] rounded-t-3xl">
        <SheetHeader className="pb-4">
          <SheetTitle className="text-center">{getTitle()}</SheetTitle>
          <p className="text-sm text-muted-foreground text-center truncate px-4">
            {taskTitle}
          </p>
        </SheetHeader>

        <div className="space-y-4 pb-6">
          {/* Text Note Input */}
          {proofType === 'TEXT_NOTE' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-3"
            >
              <Textarea
                placeholder="What did you do? Any notes..."
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                rows={4}
                className="resize-none text-base"
                autoFocus
              />
            </motion.div>
          )}

          {/* Photo Capture */}
          {proofType === 'PHOTO_UPLOAD' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <AnimatePresence mode="wait">
                {isCameraActive && !previewUrl ? (
                  <motion.div
                    key="camera"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="relative aspect-[4/3] bg-black rounded-xl overflow-hidden"
                  >
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-4 inset-x-0 flex justify-center gap-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-12 w-12 rounded-full bg-white/20 backdrop-blur"
                        onClick={() => {
                          if (streamRef.current) {
                            streamRef.current.getTracks().forEach(track => track.stop());
                          }
                          setIsCameraActive(false);
                        }}
                      >
                        <X className="h-6 w-6 text-white" />
                      </Button>
                      <Button
                        variant="default"
                        size="icon"
                        className="h-16 w-16 rounded-full"
                        onClick={capturePhoto}
                      >
                        <div className="h-12 w-12 rounded-full border-4 border-white" />
                      </Button>
                    </div>
                  </motion.div>
                ) : previewUrl ? (
                  <motion.div
                    key="preview"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="relative aspect-[4/3] bg-muted rounded-xl overflow-hidden"
                  >
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/50"
                      onClick={() => {
                        setFile(null);
                        setPreviewUrl(null);
                      }}
                    >
                      <X className="h-4 w-4 text-white" />
                    </Button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="buttons"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="grid grid-cols-2 gap-3"
                  >
                    <button
                      onClick={startCamera}
                      className={cn(
                        "flex flex-col items-center justify-center gap-3 p-6",
                        "bg-primary/5 rounded-xl border-2 border-dashed border-primary/20",
                        "hover:bg-primary/10 hover:border-primary/40 transition-all"
                      )}
                    >
                      <Camera className="h-8 w-8 text-primary" />
                      <span className="text-sm font-medium">Take Photo</span>
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className={cn(
                        "flex flex-col items-center justify-center gap-3 p-6",
                        "bg-muted rounded-xl border-2 border-dashed border-border",
                        "hover:bg-muted/80 hover:border-muted-foreground/30 transition-all"
                      )}
                    >
                      <ImageIcon className="h-8 w-8 text-muted-foreground" />
                      <span className="text-sm font-medium">From Gallery</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
            </motion.div>
          )}

          {/* File Upload */}
          {proofType === 'FILE_UPLOAD' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              {file ? (
                <div className="flex items-center gap-3 p-4 bg-muted rounded-xl">
                  <FileUp className="h-8 w-8 text-primary" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setFile(null);
                      setPreviewUrl(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "w-full flex flex-col items-center justify-center gap-3 p-8",
                    "bg-muted rounded-xl border-2 border-dashed border-border",
                    "hover:bg-muted/80 hover:border-muted-foreground/30 transition-all"
                  )}
                >
                  <FileUp className="h-10 w-10 text-muted-foreground" />
                  <span className="text-sm font-medium">Tap to upload file</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="hidden"
              />
            </motion.div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            {onSkip && (
              <Button
                variant="outline"
                className="flex-1 h-12"
                onClick={() => {
                  handleClose();
                  onSkip();
                }}
              >
                Skip
              </Button>
            )}
            <Button
              variant="hero"
              className="flex-1 h-12"
              disabled={!canSubmit || isSubmitting}
              onClick={handleSubmit}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Done
                </>
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
